import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Workflow,
  Plus,
  Search,
  Play,
  Pause,
  Trash2,
  Copy,
  MoreVertical,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Zap,
  GitBranch,
  Users,
  TrendingUp,
  Calendar,
  Filter,
  LayoutGrid,
  List,
  RefreshCw,
  Settings,
  Eye,
  Edit3,
  Sparkles,
  ArrowRight,
  Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as workflowService from '../../services/workflowService';
import UsageBadge from '../../components/ui/UsageBadge';

interface WorkflowItem {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'error';
  trigger_type?: string;
  executions_count: number;
  successful_executions: number;
  failed_executions: number;
  last_execution_at?: string;
  last_execution_status?: string;
  created_at: string;
  updated_at: string;
}

const WorkflowsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    executions: 0,
    successRate: 0,
  });

  useEffect(() => {
    loadWorkflows();
    loadStats();
  }, []);

  const loadWorkflows = async () => {
    setIsLoading(true);
    try {
      const response = await workflowService.getWorkflows();
      if (response.success) {
        setWorkflows(response.data || []);
      }
    } catch (error) {
      console.error('Error loading workflows:', error);
      toast.error(t('workflow.page.loadError', 'Error loading automations'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await workflowService.getWorkflowStats();
      if (response.success && response.data) {
        setStats({
          total: response.data.totalWorkflows,
          active: response.data.activeWorkflows,
          executions: response.data.totalExecutions,
          successRate: response.data.successRate,
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateWorkflow = () => {
    navigate('/workflows/new');
  };

  const handleEditWorkflow = (id: string) => {
    navigate(`/workflows/${id}`);
  };

  const handleDuplicateWorkflow = async (id: string) => {
    try {
      const response = await workflowService.duplicateWorkflow(id);
      if (response.success) {
        toast.success(t('workflow.page.duplicateSuccess', 'Automation duplicated successfully!'));
        loadWorkflows();
      }
    } catch (error) {
      toast.error(t('workflow.page.duplicateError', 'Error duplicating automation'));
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    if (!confirm(t('workflow.page.deleteConfirm', 'Are you sure you want to delete this automation?'))) return;

    try {
      const response = await workflowService.deleteWorkflow(id);
      if (response.success) {
        toast.success(t('workflow.page.deleteSuccess', 'Automation deleted successfully!'));
        setWorkflows(workflows.filter(w => w.id !== id));
      }
    } catch (error) {
      toast.error(t('workflow.page.deleteError', 'Error deleting automation'));
    }
  };

  const handleToggleStatus = async (workflow: WorkflowItem) => {
    const newStatus = workflow.status === 'active' ? 'paused' : 'active';
    try {
      const response = await workflowService.updateWorkflowStatus(workflow.id, newStatus);
      if (response.success) {
        toast.success(newStatus === 'active' ? t('workflow.page.activatedSuccess', 'Automation activated!') : t('workflow.page.pausedSuccess', 'Automation paused!'));
        setWorkflows(workflows.map(w =>
          w.id === workflow.id ? { ...w, status: newStatus } : w
        ));
        loadStats();
      }
    } catch (error) {
      toast.error(t('workflow.page.statusError', 'Error changing status'));
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          workflow.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || workflow.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      active: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: <CheckCircle className="w-3.5 h-3.5" />, label: t('workflow.status.active', 'Active') },
      paused: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: <Pause className="w-3.5 h-3.5" />, label: t('workflow.status.paused', 'Paused') },
      draft: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', icon: <Edit3 className="w-3.5 h-3.5" />, label: t('workflow.status.draft', 'Draft') },
      error: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: <XCircle className="w-3.5 h-3.5" />, label: t('common.error', 'Error') },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getTriggerLabel = (triggerType?: string) => {
    const triggers: Record<string, string> = {
      'landing_page': 'Landing Page',
      'trigger_landing_page': 'Landing Page',
      'form_submit': t('workflow.page.formSubmit', 'Form'),
      'webhook': 'Webhook',
      'manual': 'Manual',
      // Gatilhos sociais: apareciam como "Não configurado" na lista mesmo com o
      // fluxo configurado — o cliente achava que tinha errado (caso Turista FC).
      'instagram_first_dm': t('workflow.page.instagramFirstDm', 'Primeira DM (Instagram)'),
      'instagram_story_reply': t('workflow.page.instagramStoryReply', 'Resposta de Story (Instagram)'),
      'instagram_comment': t('workflow.page.instagramComment', 'Comentário (Instagram)'),
      'instagram_icebreaker': t('workflow.page.instagramIcebreaker', 'Ice Breaker (Instagram)'),
      'social_comment': t('workflow.page.socialComment', 'Comentário em rede social'),
      'crm_webhook': 'Webhook do CRM',
      'marketplace': 'Marketplace',
    };
    return triggers[triggerType || ''] || t('workflow.page.notConfigured', 'Not configured');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('workflow.page.never', 'Never');
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-3 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/25 flex-shrink-0">
              <Workflow className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            {t('workflow.page.title', 'Automations')}
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mt-1">
            {t('workflow.page.subtitle', 'Create visual flows to automate actions with leads and integrate with CRM')}
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <UsageBadge resource="automated_workflows" />
          <button
            onClick={handleCreateWorkflow}
            className="btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2 whitespace-nowrap text-sm bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg shadow-purple-500/25"
          >
            <Plus className="w-5 h-5 flex-shrink-0" />
            <span className="sm:hidden">{t('workflow.page.newWorkflowShort', 'Nova')}</span>
            <span className="hidden sm:inline">{t('workflow.page.newWorkflow', 'New Automation')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="card p-3 sm:p-4 min-w-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 sm:p-2.5 rounded-xl flex-shrink-0 bg-purple-100 dark:bg-purple-900/50">
              <Workflow className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium truncate">{t('workflow.page.total', 'Total')}</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="card p-3 sm:p-4 min-w-0 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 sm:p-2.5 rounded-xl flex-shrink-0 bg-green-100 dark:bg-green-900/50">
              <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium truncate">{t('workflow.page.active', 'Active')}</p>
              <p className="text-xl sm:text-2xl font-bold text-green-700 dark:text-green-300">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="card p-3 sm:p-4 min-w-0 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 sm:p-2.5 rounded-xl flex-shrink-0 bg-blue-100 dark:bg-blue-900/50">
              <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium truncate">{t('workflow.page.executions', 'Executions')}</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.executions}</p>
            </div>
          </div>
        </div>

        <div className="card p-3 sm:p-4 min-w-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 sm:p-2.5 rounded-xl flex-shrink-0 bg-amber-100 dark:bg-amber-900/50">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium truncate">{t('workflow.page.successRate', 'Success Rate')}</p>
              <p className="text-xl sm:text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.successRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card p-3 sm:p-4">
        {/* Celular: busca em cima; status + atualizar + modo de exibição embaixo */}
        <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-between">
          <div className="contents sm:flex sm:items-center sm:gap-3 sm:min-w-0 sm:flex-1">
            {/* Search */}
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('workflow.page.searchPlaceholder', 'Search automations...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 sm:flex-none min-w-0 px-3 sm:px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">{t('workflow.page.allStatuses', 'All Statuses')}</option>
              <option value="active">{t('workflow.page.activeStatus', 'Active')}</option>
              <option value="paused">{t('workflow.page.pausedStatus', 'Paused')}</option>
              <option value="draft">{t('workflow.page.draftsStatus', 'Drafts')}</option>
            </select>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={loadWorkflows}
              aria-label={t('workflow.page.refresh', 'Refresh')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
              title={t('workflow.page.refresh', 'Refresh')}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                aria-label={t('workflow.page.gridView', 'Ver em cartões')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label={t('workflow.page.listView', 'Ver em lista')}
                className={`p-2 ${viewMode === 'list' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Workflows Grid/List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center">
            <Workflow className="w-10 h-10 text-purple-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {searchQuery || statusFilter !== 'all' ? t('workflow.page.noWorkflowsFound', 'No automations found') : t('workflow.page.createFirst', 'Create your first automation')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {searchQuery || statusFilter !== 'all'
              ? t('workflow.page.adjustFilters', 'Try adjusting the search filters')
              : t('workflow.page.emptyDescription', 'Automate the lead flow by connecting your Landing Page to CRM with personalized actions')}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={handleCreateWorkflow}
              className="btn-primary inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500"
            >
              <Sparkles className="w-5 h-5" />
              {t('workflow.page.createWorkflow', 'Create Automation')}
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow.id}
              className="card group hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-purple-200 dark:hover:border-purple-800"
              onClick={() => handleEditWorkflow(workflow.id)}
            >
              <div className="p-4 sm:p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${
                      workflow.status === 'active'
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30'
                        : 'bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900'
                    }`}>
                      <GitBranch className={`w-5 h-5 ${
                        workflow.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'
                      }`} />
                    </div>
                    {getStatusBadge(workflow.status)}
                  </div>

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === workflow.id ? null : workflow.id);
                      }}
                      aria-label={t('common.actions', 'Ações')}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>

                    {activeMenu === workflow.id && (
                      <div
                        className="absolute right-0 top-8 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => { handleEditWorkflow(workflow.id); setActiveMenu(null); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Edit3 className="w-4 h-4" />
                          {t('workflow.page.edit', 'Edit')}
                        </button>
                        <button
                          onClick={() => { handleToggleStatus(workflow); setActiveMenu(null); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          {workflow.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          {workflow.status === 'active' ? t('workflow.page.pause', 'Pause') : t('workflow.page.activate', 'Activate')}
                        </button>
                        <button
                          onClick={() => { handleDuplicateWorkflow(workflow.id); setActiveMenu(null); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          {t('workflow.page.duplicate', 'Duplicate')}
                        </button>
                        <hr className="my-2 border-gray-200 dark:border-gray-700" />
                        <button
                          onClick={() => { handleDeleteWorkflow(workflow.id); setActiveMenu(null); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('workflow.page.delete', 'Delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1 break-words group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  {workflow.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">
                  {workflow.description || t('common.noDescription', 'No description')}
                </p>

                {/* Trigger */}
                <div className="flex items-center gap-2 mb-4 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {t('workflow.page.trigger', 'Trigger')}: <span className="font-medium">{getTriggerLabel(workflow.trigger_type)}</span>
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-1 sm:gap-2 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{workflow.executions_count}</p>
                    <p className="text-xs text-gray-500">{t('workflow.page.executions', 'Executions')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{workflow.successful_executions}</p>
                    <p className="text-xs text-gray-500">{t('common.success', 'Success')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-500">{workflow.failed_executions}</p>
                    <p className="text-xs text-gray-500">{t('common.failed', 'Failed')}</p>
                  </div>
                </div>

                {/* Last Execution */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {t('workflow.page.lastExecution', 'Last')}: {formatDate(workflow.last_execution_at)}
                  </span>
                  <ArrowRight className="w-4 h-4 text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.name', 'Name')}</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.status', 'Status')}</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">{t('workflow.page.trigger', 'Trigger')}</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">{t('workflow.page.executions', 'Executions')}</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">{t('workflow.page.lastExecution', 'Last Execution')}</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredWorkflows.map((workflow) => (
                <tr
                  key={workflow.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                  onClick={() => handleEditWorkflow(workflow.id)}
                >
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`hidden sm:block p-2 rounded-lg ${
                        workflow.status === 'active'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <GitBranch className={`w-4 h-4 ${
                          workflow.status === 'active' ? 'text-green-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white break-words">{workflow.name}</p>
                        <p className="text-sm text-gray-500 truncate max-w-[150px] sm:max-w-xs">{workflow.description || t('common.noDescription', 'No description')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">{getStatusBadge(workflow.status)}</td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden md:table-cell">{getTriggerLabel(workflow.trigger_type)}</td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 hidden md:table-cell">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-900 dark:text-white font-medium">{workflow.executions_count}</span>
                      <span className="text-green-600">{workflow.successful_executions} {t('common.ok', 'ok')}</span>
                      <span className="text-red-500">{workflow.failed_executions} {t('common.error', 'error')}</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">{formatDate(workflow.last_execution_at)}</td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-right">
                    <div className="flex items-center justify-end gap-0.5 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleStatus(workflow)}
                        className={`p-2 rounded-lg transition-colors ${
                          workflow.status === 'active'
                            ? 'hover:bg-yellow-100 text-yellow-600'
                            : 'hover:bg-green-100 text-green-600'
                        }`}
                        title={workflow.status === 'active' ? t('workflow.page.pause', 'Pause') : t('workflow.page.activate', 'Activate')}
                      >
                        {workflow.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDuplicateWorkflow(workflow.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                        title={t('workflow.page.duplicate', 'Duplicate')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteWorkflow(workflow.id)}
                        className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500"
                        title={t('workflow.page.delete', 'Delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CRM Info Banner */}
      <div className="card p-4 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-800">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:contents">
            <div className="p-3 rounded-xl bg-pink-100 dark:bg-pink-900/50 flex-shrink-0">
              <Link2 className="w-6 h-6 text-pink-600 dark:text-pink-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white sm:hidden">{t('workflow.page.crmIntegration', 'CRM Integration')}</h4>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 dark:text-white hidden sm:block">{t('workflow.page.crmIntegration', 'CRM Integration')}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('workflow.page.crmIntegrationDesc', 'WhatsApp, Email, and SMS are sent through CRM integration. Configure the CRM API Key in each automation.')}
            </p>
          </div>
          <a
            href="https://crm.devskin.com/api-docs.html"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm w-full sm:w-auto text-center flex-shrink-0"
          >
            {t('workflow.page.viewDocs', 'View Documentation')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default WorkflowsPage;
